<template>
  <div class="flex flex-col">
    <div class="flex justify-between items-center">
      <dropdown-button name="gptVersion"
      :options="gptVersionOptions"
      :primary-button-variant="isDisabled ? 'dropdown-header-disabled' : 'dropdown-header'"
      :close-on-select="true"
      :value="resolvedDeploymentId"
      :key="actionId"
      primary-button-classes="w-72"
      arrow-color="black-a20"
      @select-button="updateGptVersion"
      :is-disabled="isDisabled"
      />

      <span v-if="isDisabled && !hideSections" class="text-gray-dark text-xs">(read-only)</span>
    </div>

    <BaseCallout v-if="isEmailBot && !isDisabled"
      class="mt-4"
      text='If the AI reviews an email query and suggests handing it over to an operator, then the bot will NOT send an email response.<br/><br/>
        Use condition case "AI Call Operator" to send an email response and/or call an operator.'
    />

    <with-error-message :show-error="isIndexIdError" class="mt-4 relative">
      <base-label :id="KNOWLEDGE_BASE.id"
                  :label="KNOWLEDGE_BASE.label"
                  :tooltip="isProductRecommendation ? KNOWLEDGE_BASE.productTooltip : KNOWLEDGE_BASE.tooltip"
      />
      <div v-if="!isLoading && knowledgeBaseOptions.length === 0" class="h-10 mt-1 flex items-center">
        <span>No product feed yet.</span>
        <router-link :to="{ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: ITEM_IDS.NEW } }"
                     class="ml-1 underline hover:text-black">
          Create one?
        </router-link>
      </div>
      <base-select v-else
                   :variant="knowledgeBaseSelectVariant"
                   :value="selectedKnowledgeBaseKey"
                   :options="knowledgeBaseOptions"
                   :value-attribute="'selectionKey'"
                   :text-attribute="'name'"
                   :is-disabled="isDisabled"
                   class="w-full border-gray mt-1 h-10"
                   @input="updateKnowledgeBaseSelection"
      />
      <router-link v-if="selectedKnowledgeBaseRoute"
                   :to="selectedKnowledgeBaseRoute"
                   class="mt-1 flex items-center gap-1 text-xs"
      >
        <span class="hover:underline">View Knowledge Base</span>
        <vs-icon icon="call_made" size="1.2rem"/>
      </router-link>
      <div v-if="!hideSections && (showKnowledgeBaseNotReadyWarning || selectedIndexGroupMissingMarkets)"
           class="absolute left-0 mt-0.5 flex flex-col gap-0.5"
      >
        <vs-tooltip v-if="showKnowledgeBaseNotReadyWarning" position="top">
          <template #content>
            <div v-html="'Knowledge base creation is in progress. When ready, the bot will be able to use it.'"/>
          </template>
          <div class="text-xs text-gray-dark">
            ⚠️ Knowledge base is not ready.
          </div>
        </vs-tooltip>
        <vs-tooltip v-if="selectedIndexGroupMissingMarkets" position="top">
          <template #content>
            <div v-html="selectedIndexGroupMissingMarkets.tooltip"/>
          </template>
          <router-link v-if="selectedIndexGroupMissingMarkets.route"
                       :to="selectedIndexGroupMissingMarkets.route"
                       class="text-xs text-gray-dark underline hover:text-black"
          >
            ⚠️ Add missing markets
          </router-link>
        </vs-tooltip>
      </div>
    </with-error-message>

    <chat-GPT-editor-product-recommendation v-if="!hideSections && isProductRecommendation"
                                            class="mt-4"
                                            :action="action"
                                            :action-id="actionId"
                                            :is-disabled="isDisabled"
                                            :is-email-bot="isEmailBot"
    />

    <form @input="updateAction" class="mt-4 flex flex-col gap-4">
      <chat-GPT-editor-form-row-full v-if="!hideSections"
                                     :item="isProductRecommendation ? PRODUCT_INSTRUCTIONS : INSTRUCTIONS"
                                     :is-item-error="isPromptError"
                                     :row-count="isProductRecommendation ? 6 : 20"
                                     :token-count="promptTokenCount"
                                     :placeholder="isDisabled || !isProductRecommendation ? '' : PRODUCT_INSTRUCTIONS.placeholder"
                                     :is-disabled="isDisabled"
                                     :action="displayAction"
      />

      <chat-GPT-editor-form-row-split v-if="showUnreleased && isActionTypeChatGptCustom"
                                      :first-item="TOP_N_DOCUMENTS"
                                      :is-first-item-error="isTopNDocumentsError"
                                      :is-first-item-admin="true"
                                      :is-disabled="isDisabled"
                                      :action="action"
      />

      <chat-GPT-editor-tools v-if="!isProductRecommendation"
                             :action="action"
                             :action-id="actionId"
                             :is-disabled="isDisabled"
      />

      <template v-if="!isProductRecommendation">
        <vx-tooltip text="Lets the AI collect exact values it needs for a tool (email, phone, order number, …) through validated input fields instead of free text. Webchat only; the answer should end with “Continue or Find answer” so the reply returns to this action."
                    position="top"
                    class="flex items-center"
        >
          <checkbox id="allowInputFieldsCheckbox"
                    :is-checked="allowInputFields"
                    :is-disabled="isDisabled"
                    @update:isChecked="handleAllowInputFieldsCheckbox"
          />
          <label for="allowInputFieldsCheckbox"
                 class="ml-2 text-gray-dark cursor-pointer leading-tight"
                 :class="isDisabled ? 'cursor-not-allowed' : 'cursor-default'"
          >
            Allow LLM to use structured input fields
          </label>
        </vx-tooltip>
      </template>

    </form>

    <base-button v-if="!isDisabled" variant="bordered-error" class="w-full mt-8" @click="removeChatGPT">
      {{ removeLabel }}
    </base-button>
  </div>
</template>

<script>
import answerController from '@/controllers/answer.controller'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import stateTreeUtils from '@/utils/state-tree.utils'
import templateController from '@/controllers/template.controller'
import templateModule from '@/store/modules/template.module'
import { ACTION_TYPES, GPT_VERSIONS, INDEX_TYPES } from '@/utils/bot-json.constants'
import { ITEM_IDS } from '@/utils/editor.constants'
import { CHAT_GPT_EDITOR_PROPS } from '@/constants/chat-gpt.constants'
import { ROUTE_NAMES } from '@/router'
import dropdownButtonsUtils from '@/utils/dropdown-button.utils'
import Checkbox from '@/components/preview/Checkbox'
import DropdownButton from '@/components/common/DropdownButton'
import ChatGPTEditorFormRowFull from '@/components/editor/ChatGPTEditorFormRowFull'
import ChatGPTEditorFormRowSplit from '@/components/editor/ChatGPTEditorFormRowSplit'
import ChatGPTEditorTools from '@/components/editor/ChatGPTEditorTools'
import ChatGPTEditorProductRecommendation from '@/components/editor/ChatGPTEditorProductRecommendation'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import $script from 'scriptjs'
import indexesService from '@/services/amio/indexes.service'
import indexGroupService from '@/services/amio/index-group.service'
import marketService from '@/services/amio/market.service'
import botModule from '@/store/modules/bot.module'
import marketModule from '@/store/modules/market.module'
import {
  buildMissingKnowledgeMarketsTooltip,
  getFirstIndexIdForIndexGroup,
  getMissingKnowledgeMarketsForIndexGroup,
  isProductFeedIndex
} from '@/utils/index.utils'
import { isProductRecommendationAction, mergeLegacyPrompts } from '@/utils/chat-gpt-action.utils'
import { getMarketsForCurrentBot } from '@/utils/market.utils'

const NO_KNOWLEDGE_BASE_OPTION = Object.freeze({ selectionKey: '', name: 'None (general answer)' })

const {
  KNOWLEDGE_BASE,
  INSTRUCTIONS,
  PRODUCT_INSTRUCTIONS,
  TOP_N_DOCUMENTS
} = CHAT_GPT_EDITOR_PROPS

export default {
  name: 'ChatGPTEditor',

  components: {
    Checkbox,
    DropdownButton,
    ChatGPTEditorFormRowFull,
    ChatGPTEditorFormRowSplit,
    ChatGPTEditorTools,
    ChatGPTEditorProductRecommendation,
    WithErrorMessage
  },

  props: {
    isDisabled: {
      type: Boolean,
      default: false
    },
    hideSections: {
      type: Boolean,
      default: false
    },
    cachedAction: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      ROUTE_NAMES,
      ITEM_IDS,
      isLoading: false,
      indexes: [],
      indexGroups: [],
      toggleRefreshKey: 0,
      tokenizer: null,
      KNOWLEDGE_BASE,
      INSTRUCTIONS,
      PRODUCT_INSTRUCTIONS,
      TOP_N_DOCUMENTS
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,
    stateTree: chatBubbleModule.stateTree,
    template: templateModule.template,

    actionIndexId() {
      return this.action?.indexId || ''
    },

    resolvedIndexGroupId() {
      if (!this.actionIndexId) return null

      const selectedIndex = this.indexes.find(index => index.id === this.actionIndexId)
      if (selectedIndex?.index_group_id) return selectedIndex.index_group_id

      if (this.isIndexTypeGroup) return this.actionIndexId

      return null
    },

    selectedKnowledgeBaseKey() {
      if (!this.actionIndexId) return ''

      if (this.resolvedIndexGroupId) {
        return `${INDEX_TYPES.GROUP}:${this.resolvedIndexGroupId}`
      }

      return `${INDEX_TYPES.SINGLE}:${this.actionIndexId}`
    },

    selectedKnowledgeBaseRoute() {
      if (!this.actionIndexId) return null

      let knowledgeBaseId = this.actionIndexId
      if (this.resolvedIndexGroupId) {
        const botMarketId = botModule.getBot()?.market_id
        const marketIndex = this.indexes.find(index =>
          index.index_group_id === this.resolvedIndexGroupId && index.market_id === botMarketId
        )
        knowledgeBaseId = marketIndex?.id || getFirstIndexIdForIndexGroup(this.indexes, this.resolvedIndexGroupId)
      }

      if (!knowledgeBaseId) return null

      return {
        name: ROUTE_NAMES.knowledgeBases,
        params: { knowledgeBaseId }
      }
    },

    standaloneIndexes() {
      return this.indexes.filter(index => !index.index_group_id)
    },

    isEmailBot() {
      return botModule.isEmailBot()
    },

    botHasMarket() {
      return !!botModule.getBot().market_id
    },

    multiBotMarkets() {
      return getMarketsForCurrentBot()
    },

    isIndexTypeGroup() {
      return this.action?.indexType === INDEX_TYPES.GROUP
    },

    knowledgeBaseOptions() {
      const options = []
      const includedGroupIds = new Set()
      const shouldIncludeIndexGroups = this.botHasMarket || this.hideSections || this.isDisabled

      if (shouldIncludeIndexGroups) {
        this.indexGroups.forEach(group => {
          includedGroupIds.add(group.id)
          options.push({
            selectionKey: `${INDEX_TYPES.GROUP}:${group.id}`,
            id: group.id,
            name: group.name,
            indexType: INDEX_TYPES.GROUP
          })
        })

        this.indexes.forEach(index => {
          const groupId = index.index_group_id
          if (!groupId || includedGroupIds.has(groupId)) return

          includedGroupIds.add(groupId)
          const group = this.indexGroups.find(item => item.id === groupId)
          options.push({
            selectionKey: `${INDEX_TYPES.GROUP}:${groupId}`,
            id: groupId,
            name: group?.name || index.name,
            indexType: INDEX_TYPES.GROUP
          })
        })
      }

      this.standaloneIndexes.forEach(index => {
        options.push({
          selectionKey: `${INDEX_TYPES.SINGLE}:${index.id}`,
          id: index.id,
          name: index.name,
          indexType: INDEX_TYPES.SINGLE
        })
      })

      const sorted = options.sort((a, b) => a.name.localeCompare(b.name))
      if (!this.isProductRecommendation) return [NO_KNOWLEDGE_BASE_OPTION, ...sorted]

      // The saved selection stays listed even if it no longer qualifies, so the select never shows blank
      return sorted.filter(option =>
        this.productFeedSelectionKeys.has(option.selectionKey) || option.selectionKey === this.selectedKnowledgeBaseKey)
    },

    isProductRecommendation() {
      return isProductRecommendationAction(this.action)
    },

    removeLabel() {
      return this.isProductRecommendation ? 'Remove product recommendations' : 'Remove AI answer'
    },

    // PRO-740: the selection keys of knowledge bases holding a product feed. A market group counts when any of
    // its markets has one — a feed may exist for some markets only.
    productFeedSelectionKeys() {
      return new Set(this.indexes.filter(isProductFeedIndex).map(index => (index.index_group_id ?
        `${INDEX_TYPES.GROUP}:${index.index_group_id}` :
        `${INDEX_TYPES.SINGLE}:${index.id}`)))
    },

    isKnowledgeBaseReady() {
      if (!this.actionIndexId) return true

      if (this.resolvedIndexGroupId) {
        const groupIndexes = this.indexes.filter(index => index.index_group_id === this.resolvedIndexGroupId)
        if (!groupIndexes.length) return true

        return groupIndexes.every(index => 'provider_status' in index)
      }

      const index = this.indexes.find(index => index.id === this.actionIndexId)
      return index && 'provider_status' in index
    },

    showKnowledgeBaseNotReadyWarning() {
      return !this.isKnowledgeBaseReady
    },

    selectedIndexGroupMissingMarkets() {
      if (!this.resolvedIndexGroupId) return null

      const missingMarkets = getMissingKnowledgeMarketsForIndexGroup(this.indexes, this.resolvedIndexGroupId, this.multiBotMarkets)
      if (!missingMarkets.length) return null

      const firstIndexId = getFirstIndexIdForIndexGroup(this.indexes, this.resolvedIndexGroupId)
      return {
        tooltip: buildMissingKnowledgeMarketsTooltip(missingMarkets),
        route: firstIndexId ?
          {
            name: ROUTE_NAMES.knowledgeBases,
            params: { knowledgeBaseId: firstIndexId }
          } :
          null
      }
    },

    knowledgeBaseSelectVariant() {
      if (this.isDisabled) return 'baseSelectDisabled'

      return this.isIndexIdError ? 'baseSelectError' : 'baseSelect'
    },

    isIndexIdError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Please select a knowledge base.'
    },

    isDeploymentIdError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'GPT Version value is not valid!'
    },

    // TODO Workaround to show the error. Real fix in https://github.com/amio-io/amio-chatbot-engine/issues/1286
    isPromptError() {
      return chatBubbleModule.isErrorBubbleFocused() && (this.errorDescription === 'Chat GPT prompt is not allowed to be empty!' || this.error.path === 'prompt')
    },

    isTopNDocumentsError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Top N Documents value is not valid!'
    },

    actionId() {
      return this.bubbleSelected.actionId
    },

    action() {
      return this.cachedAction || conversationModule.getAction(this.bubbleSelected.stateId, this.actionId)
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    },

    isActionTypeChatGptCustom() {
      return this.action.type === ACTION_TYPES.CHAT_GPT_CUSTOM
    },

    // PRO-672: the request_input tool — offered to the model only when this is on
    allowInputFields() {
      return !!this.action.allowInputFields
    },

    gptVersionOptions() {
      const options = dropdownButtonsUtils.getGptVersionOptions()
      if (this.showUnreleased) return options

      return options.filter(option => {
        if (this.resolvedDeploymentId === option.value) return true
        if ([
          GPT_VERSIONS.FIVE_POINT_SIX_TERRA,
          GPT_VERSIONS.FIVE_POINT_SIX_SOL,
          GPT_VERSIONS.SIX_SOL
        ].includes(option.value)) return false

        return option.value !== GPT_VERSIONS.FOUR_OMNI_EU &&
          (!option.value.startsWith('gpt-5') || option.value === GPT_VERSIONS.FIVE_POINT_SIX_LUNA) &&
          (!option.value.startsWith('gpt-6') || option.value === GPT_VERSIONS.SIX_LUNA)
      })
    },

    resolvedDeploymentId() {
      switch (this.action.deploymentId) {
        case GPT_VERSIONS.FOUR_OMNI:
        case GPT_VERSIONS.FOUR_OMNI_GLOBAL:
          return GPT_VERSIONS.FOUR_OMNI_EU
        case GPT_VERSIONS.FOUR_TURBO:
        case GPT_VERSIONS.FOUR:
        case GPT_VERSIONS.THREE_POINT_FIVE:
          return GPT_VERSIONS.FOUR_OMNI_MINI
        default:
          return this.action.deploymentId
      }
    },

    isGpt5Deployment() {
      return this.resolvedDeploymentId?.startsWith('gpt-5') || this.resolvedDeploymentId?.startsWith('gpt-6')
    },

    // PRO-740: an old action's user prompt is shown appended to its Instructions (merged for real on its next edit)
    displayAction() {
      return mergeLegacyPrompts(this.action)
    },

    promptTokenCount() {
      const prompt = this.displayAction?.prompt
      if (!prompt) return 0

      return this.tokenizer?.encode(prompt).length || 0
    }
  },

  methods: {
    removeChatGPT() {
      const node = stateTreeUtils.findNode(this.stateTree, this.bubbleSelected)
      this.previewController.removeBubble(node, this.template?.id)
    },

    updateKnowledgeBaseSelection(selectionKey) {
      if (this.isDisabled) return

      const tools = this.action.tools || []
      if (!selectionKey) {
        conversationModule.updateChatGPTAction(this.actionId, { ...conversationModule.createChatGPTGeneralAction(this.action), tools })
        return
      }

      const option = this.knowledgeBaseOptions.find(item => item.selectionKey === selectionKey)
      if (!option) return

      const action = this.action.type === ACTION_TYPES.CHAT_GPT_CUSTOM ?
        this.action :
        { ...conversationModule.createChatGPTCustomAction(this.action, true), tools }
      conversationModule.updateChatGPTAction(this.actionId, { ...action, indexId: option.id, indexType: option.indexType })
      if (this.isIndexIdError) this.error = null
    },

    handleAllowInputFieldsCheckbox(allowInputFields) {
      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, allowInputFields })
    },

    updateGptVersion(index) {
      if (this.isDisabled) return

      const { value: deploymentId } = this.gptVersionOptions[index]
      const updatedAction = { ...this.action, deploymentId }
      conversationModule.updateChatGPTAction(this.actionId, updatedAction)
      this.toggleRefreshKey = Math.random()
      this.resetError('deploymentId', deploymentId)
    },

    updateAction({ target }) {
      if (this.isDisabled) return
      if (target.id === 'allowInputFieldsCheckbox') return

      const valueToSave = this.resolveValueToSave(target)
      // PRO-740: Instructions already shows the legacy fields merged in, so what is typed there replaces them all
      const action = target.name === this.INSTRUCTIONS.id ? mergeLegacyPrompts(this.action) : this.action
      conversationModule.updateChatGPTAction(this.actionId, { ...action, [target.name]: valueToSave })
      this.resetError(target.name, valueToSave)
    },

    resetError(name, value) {
      const isDeploymentIdFixed = this.isDeploymentIdError && name === 'deploymentId' && this.gptVersionOptions.some(option => option.value === value)
      const isPromptFixed = this.isPromptError && name === this.INSTRUCTIONS.id && value.length > 0
      const isTopNDocumentsFixed = this.isTopNDocumentsError && name === this.TOP_N_DOCUMENTS.id && value >= this.TOP_N_DOCUMENTS.min && value <= this.TOP_N_DOCUMENTS.max && Number.isInteger(value)
      if (isDeploymentIdFixed || isPromptFixed || isTopNDocumentsFixed) {
        this.error = null
      }
    },

    resolveValueToSave(target) {
      const { name, value, min, max } = target
      if (name === this.INSTRUCTIONS.id) return value

      return Number(this.resolveRange(value, min, max))
    },

    resolveRange(value, min, max) {
      if (+value > max) return max
      if (+value < min) return min

      return +value
    },

    async initTokenizer() {
      if (!window.GPTTokenizer_cl100k_base) {
        await new Promise(resolve => $script('https://unpkg.com/gpt-tokenizer', resolve))
      }
      this.tokenizer = window.GPTTokenizer_cl100k_base
    }
  },

  async mounted() {
    this.initTokenizer()
    this.isLoading = true
    try {
      const requests = [
        indexesService.list(),
        indexGroupService.list().catch(() => [])
      ]
      const indexGroupsIdx = 1

      let marketsIdx = null
      if (this.botHasMarket) {
        marketsIdx = requests.length
        requests.push(marketService.list().catch(() => []))
      }

      const results = await Promise.all(requests)

      this.indexes = results[0].sort((a, b) => a.name.localeCompare(b.name))
      this.indexGroups = results[indexGroupsIdx].sort((a, b) => a.name.localeCompare(b.name))
      if (marketsIdx !== null) {
        marketModule.setMarketList(results[marketsIdx] || [])
      }
    } finally {
      this.isLoading = false
    }

    // PRO-740: a new product answer with exactly one product feed to choose from gets it picked already
    if (this.isProductRecommendation && !this.isDisabled && !this.actionIndexId && this.knowledgeBaseOptions.length === 1) {
      this.updateKnowledgeBaseSelection(this.knowledgeBaseOptions[0].selectionKey)
    }
  }
}
</script>
